import { useEffect, useId, useRef, useState } from 'react';
import { LocateFixed, MapPin, X } from 'lucide-react';
import { api } from '../lib/api';
import { locateOnce } from '../lib/locate';
import { MY_LOCATION } from '../lib/tripEnds';
import type { GeocodeResult, Waypoint } from '../lib/types';

interface Props {
  label: string;
  value: Waypoint | null;
  onChange: (w: Waypoint | null) => void;
  placeholder?: string;
  onRemove?: () => void;
  /** Offer a "Use my location" button; it picks a point labelled "My location". */
  locate?: boolean;
}

/** Type a town or address, pick from suggestions. */
export default function PlaceSearch({ label, value, onChange, placeholder = 'Town, city or address', onRemove, locate }: Props) {
  const id = useId();
  const listId = `${id}-list`;
  const [text, setText] = useState(value?.label ?? '');
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => setText(value?.label ?? ''), [value?.label]);

  useEffect(() => {
    window.clearTimeout(timer.current);
    const q = text.trim();
    if (q.length < 3 || q === value?.label) {
      setResults([]);
      return;
    }
    timer.current = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.geocode(q);
        setResults(r);
        setActive(r.length ? 0 : -1);
        setOpen(true);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Place search failed');
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => window.clearTimeout(timer.current);
  }, [text, value?.label]);

  const pick = (r: GeocodeResult) => {
    const w = { label: r.shortLabel ?? r.label, lat: r.lat, lng: r.lng };
    onChange(w);
    setText(w.label);
    setOpen(false);
  };

  const useMyLocation = async () => {
    setError(null);
    setLocating(true);
    try {
      const at = await locateOnce();
      onChange({ label: MY_LOCATION, ...at });
      setText(MY_LOCATION);
      setOpen(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLocating(false);
    }
  };
  const mine = value?.label === MY_LOCATION;

  return (
    <div className="relative flex flex-col gap-1.5">
      <label htmlFor={id}>{label}</label>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MapPin size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-dim" aria-hidden />
          <input
            id={id}
            type="text"
            role="combobox"
            aria-expanded={open && results.length > 0}
            aria-controls={listId}
            aria-autocomplete="list"
            autoComplete="off"
            className={locate ? 'pl-9 pr-12' : 'pl-9'}
            placeholder={placeholder}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              if (value) onChange(null);
            }}
            onFocus={() => results.length && setOpen(true)}
            onBlur={() => window.setTimeout(() => setOpen(false), 150)}
            onKeyDown={(e) => {
              if (!open || !results.length) return;
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActive((a) => Math.min(a + 1, results.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActive((a) => Math.max(a - 1, 0));
              } else if (e.key === 'Enter' && active >= 0) {
                e.preventDefault();
                pick(results[active]);
              } else if (e.key === 'Escape') {
                setOpen(false);
              }
            }}
          />
          {/* Inside the field, like caleventure's search: tap the crosshair to start from here. */}
          {locate && (
            <button
              type="button"
              className={`absolute right-1 top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full transition-colors hover:bg-panel-2 disabled:opacity-50 ${mine ? 'text-dd' : 'text-amber'}`}
              aria-label="Use my location"
              title="Use my location"
              disabled={locating}
              onClick={() => void useMyLocation()}
            >
              {locating ? (
                <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden />
              ) : (
                <LocateFixed size={18} aria-hidden />
              )}
            </button>
          )}
        </div>
        {onRemove && (
          <button type="button" className="btn w-11 px-0" aria-label={`Remove ${label}`} onClick={onRemove}>
            <X size={18} aria-hidden />
          </button>
        )}
      </div>
      {loading && <span className="text-[13px] text-dim">Searching…</span>}
      {locating && <span className="text-[13px] text-dim">Finding you…</span>}
      {error && <span className="text-[13px] text-danger">{error}</span>}
      {value && <span className="text-[13px] text-ok">✓ {value.label}</span>}

      {open && results.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          className="absolute left-0 right-0 top-full z-[1100] mt-1 max-h-72 overflow-auto rounded-xl border border-line-2 bg-panel p-1 shadow-xl"
        >
          {results.map((r, i) => (
            <li
              key={`${r.lat},${r.lng},${i}`}
              role="option"
              aria-selected={i === active}
              className={`cursor-pointer rounded-lg px-3 py-2 text-sm ${i === active ? 'bg-panel-2 text-amber-hi' : 'text-cream'}`}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(r);
              }}
              onMouseEnter={() => setActive(i)}
            >
              <span className="font-semibold">{r.shortLabel ?? r.label}</span>
              {r.shortLabel && <span className="block truncate text-[12px] text-dim">{r.label}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
